import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../../services/ReactQuery/reactQueryService";
import { useCase } from "../../../provider/CaseProvider";
import { Box, Card, CardContent, IconButton, Stack } from "@mui/material";
import ExecutionStepAccordion from "./ExecutionStepAccordion";
import { ExecutionJob } from "../../../types/ExecutionJob/ExecutionJob.ts";
import { AutoAwesomeRounded, DeleteOutlineOutlined } from "@mui/icons-material";
import { apiPost } from "../../../services/Api/apiCall";
import ButtonLoading from "../../Button/ButtonLoading";
import { useSnackbar } from "notistack";
import { useAiChat } from "../../../provider/AiChatProvider";

type PendingExecutionJobCardProps = {
  executionJob: ExecutionJob;
};

export default function PendingExecutionJobCard({ executionJob }: PendingExecutionJobCardProps) {
  const [isLoading, setIsLoading] = useState(false);

  const { product } = useCase();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { activeThreadId, closeStepEditDrawer } = useAiChat();

  if (!product || !executionJob) {
    return null;
  }

  //refresh ai chat messages because thats the origin of the executionjobs
  const refreshAiChatMessages = async () => {
    if (!activeThreadId) {
      return;
    }
    await queryClient.invalidateQueries(
      {
        queryKey: queryKeys.collection("ai_chat_messages", {
          aiChatThread: activeThreadId,
        })
      }
    );
  };

  const rejectJob = async () => {
    closeStepEditDrawer();
    setIsLoading(true);
    await apiPost(`/execution_jobs/${executionJob.id}/reject`);
    await refreshAiChatMessages();
    enqueueSnackbar("Job wurde abgelehnt.", {
      variant: "custom",
      isNonInteractive: true,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center",
      },
    });
    setIsLoading(false);
  };

  const executeJob = async () => {
    closeStepEditDrawer();
    setIsLoading(true);
    enqueueSnackbar("Job-Ausführung wurde gestartet.", {
      variant: "custom",
      isNonInteractive: true,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center",
      },
    });
    await apiPost(`/execution_jobs/${executionJob.id}/execute`);
    await refreshAiChatMessages();
    dispatchEvent(new Event("refetchPageData"));
    setIsLoading(false);
  };

  return (
    <Card sx={{ margin: "1rem" }}>
      <CardContent>
        {executionJob.steps.map((step) => (
          <Box key={step.id} sx={{ marginY: ".5rem" }}>
            <ExecutionStepAccordion step={step} refreshAiChatMessages={refreshAiChatMessages} />
          </Box>
        ))}
        <Stack direction={"row"} spacing={1} sx={{ marginTop: "1rem" }}>
          <IconButton onClick={rejectJob} disabled={isLoading} color={"error"}>
            <DeleteOutlineOutlined />
          </IconButton>
          <ButtonLoading
            fullWidth
            variant={"contained"}
            startIcon={<AutoAwesomeRounded />}
            onClick={executeJob}
            isLoading={isLoading}
          >
            {executionJob.steps.filter((step) => step.status !== "skipped").length} Aktionen ausführen
          </ButtonLoading>
        </Stack>
      </CardContent>
    </Card>
  );
}
